import * as z from 'zod/mini';
import { snap } from '../geometry/span';
import { HOLIDAY_WINDOW_DEFAULT_DAYS, LEAD_DAYS_LIST_MAX, LEAD_DAYS_MAX, LIMITS } from './constants';
import { normalizeLeadDays } from './important-date';
import { MinUnit, ReminderLead, ReminderScope, TimeStep } from './enums';

/** Settings that sync with data (data dictionary §3, "setting"). */
export const SettingKey = {
  /** Legacy key: read-only when both new keys are missing; new versions never write it. */
  MinUnit: 'minUnit',
  TimeStepMin: 'timeStepMin',
  MinDurationMin: 'minDurationMin',
  DefaultDurationMin: 'defaultDurationMin',
  DayStartMin: 'dayStartMin',
  SleepStartMin: 'sleepStartMin',
  SleepEndMin: 'sleepEndMin',
  DraftRepeatDays: 'draftRepeatDays',
  DraftRepeatMinHits: 'draftRepeatMinHits',
  DraftExpiryHours: 'draftExpiryHours',
  ReminderScope: 'reminderScope',
  ReminderLeadMin: 'reminderLeadMin',
  /** Frozen day start that interprets check-in data written before occurrences (data dictionary §3.2). */
  CheckinLegacyDayStartMin: 'checkinLegacyDayStartMin',
  /** Child projects unused for this many personal dates fold under "earlier" in pickers (data dictionary §3). */
  ChildFoldDays: 'childFoldDays',
  /** Lead days preselected for a new important date (data dictionary §3). */
  DefaultLeadDays: 'defaultLeadDays',
  /** Enabled holiday packs (data dictionary §3). */
  HolidayPacks: 'holidayPacks',
  /** Holidays within this many days show in the upcoming card (data dictionary §3). */
  HolidayWindowDays: 'holidayWindowDays',
} as const;
export type SettingKey = (typeof SettingKey)[keyof typeof SettingKey];

/** Allowed values of `minDurationMin` (data dictionary §3); the legal ones are multiples of the step. */
export const MIN_DURATION_CHOICES: readonly number[] = [5, 10, 15, 20, 30, 45, 60, 90, 120];

/** The `minDurationMin` choices legal under `stepMin` (its multiples), ordered ascending. */
export function minDurationChoicesFor(stepMin: number): number[] {
  return MIN_DURATION_CHOICES.filter((choice) => choice % stepMin === 0);
}

/**
 * The smallest legal minimum duration under `stepMin` that is not below `minDurationMin`
 * (data dictionary §3 normalisation rule ①): raising the step can leave a stored minimum
 * duration that is no longer a multiple of it.
 */
export function normalizeMinDuration(stepMin: number, minDurationMin: number): number {
  const legal = minDurationChoicesFor(stepMin);
  return legal.find((choice) => choice >= minDurationMin) ?? legal[legal.length - 1] ?? minDurationMin;
}

/** Range of `childFoldDays` (data dictionary §3, setting). */
export const CHILD_FOLD_DAYS_MIN = 1;
export const CHILD_FOLD_DAYS_MAX = 365;

/** Range of `holidayWindowDays` (data dictionary §3, setting). */
export const HOLIDAY_WINDOW_DAYS_MIN = 0;
export const HOLIDAY_WINDOW_DAYS_MAX = 365;

export const SETTING_VALUE_SCHEMAS = {
  minUnit: z.enum(MinUnit),
  timeStepMin: z.enum(TimeStep),
  minDurationMin: z
    .int()
    .check(
      z.refine(
        (value) => MIN_DURATION_CHOICES.includes(value),
        'minDurationMin must be one of 5, 10, 15, 20, 30, 45, 60, 90, 120',
      ),
    ),
  defaultDurationMin: z.int().check(z.minimum(15), z.maximum(1440)),
  dayStartMin: z.int().check(
    z.minimum(0),
    z.maximum(1439),
    z.refine((value) => value % 60 === 0, 'dayStartMin must be a multiple of 60'),
  ),
  sleepStartMin: z.int().check(z.minimum(0), z.maximum(2880)),
  sleepEndMin: z.int().check(z.minimum(0), z.maximum(2880)),
  draftRepeatDays: z.int().check(z.minimum(1), z.maximum(14)),
  draftRepeatMinHits: z.int().check(z.minimum(1), z.maximum(14)),
  draftExpiryHours: z.int().check(z.minimum(1), z.maximum(720)),
  reminderScope: z.enum(ReminderScope),
  reminderLeadMin: z.enum(ReminderLead),
  checkinLegacyDayStartMin: z.int().check(z.minimum(0), z.maximum(1439)),
  childFoldDays: z.int().check(z.minimum(CHILD_FOLD_DAYS_MIN), z.maximum(CHILD_FOLD_DAYS_MAX)),
  defaultLeadDays: z
    .array(z.int().check(z.minimum(0), z.maximum(LEAD_DAYS_MAX)))
    .check(z.maxLength(LEAD_DAYS_LIST_MAX)),
  holidayPacks: z
    .array(z.string().check(z.minLength(1), z.maxLength(LIMITS.holidayPackIdMax)))
    .check(z.maxLength(LIMITS.holidayPacksMax)),
  holidayWindowDays: z
    .int()
    .check(z.minimum(HOLIDAY_WINDOW_DAYS_MIN), z.maximum(HOLIDAY_WINDOW_DAYS_MAX)),
} as const satisfies Record<SettingKey, z.ZodMiniType>;

export type Settings = { [K in SettingKey]: z.infer<(typeof SETTING_VALUE_SCHEMAS)[K]> };

export const SETTING_DEFAULTS: Readonly<Settings> = {
  minUnit: MinUnit.Min30,
  timeStepMin: TimeStep.Min30,
  minDurationMin: 30,
  defaultDurationMin: 120,
  dayStartMin: 360,
  sleepStartMin: 1440,
  sleepEndMin: 1800,
  draftRepeatDays: 3,
  draftRepeatMinHits: 2,
  draftExpiryHours: 48,
  reminderScope: ReminderScope.All,
  reminderLeadMin: ReminderLead.Min5,
  // Placeholder: an absent key resolves to the current day start (resolveSettings).
  checkinLegacyDayStartMin: 360,
  childFoldDays: 15,
  defaultLeadDays: [0, 3],
  holidayPacks: [],
  holidayWindowDays: HOLIDAY_WINDOW_DEFAULT_DAYS,
};

export type SettingValue<K extends SettingKey> = Settings[K];

/**
 * `data` of a setting record at the record boundary: `{ key, value }` with a bounded `key`, the
 * `value` unvalidated and unknown fields kept. A record written under a key this build does not
 * know (by a newer client) thus survives merge, import and export instead of being skipped for
 * good (2026-10b iteration §2.2, release stage one); `SCHEMA_VERSION` stays 1. The per-key value
 * check happens where settings are read (`resolveSettings`) and written (storage `setSetting`).
 */
export const settingDataSchema = z.looseObject({
  key: z.string().check(z.minLength(1), z.maxLength(LIMITS.settingKeyMax)),
  value: z.unknown(),
});

/** A setting record's `data` as stored: any bounded key, any value. */
export type SettingRecordData = z.infer<typeof settingDataSchema>;

/** `{ key, value }` of one known key: the compile-time shape every writer of this build uses. */
export type SettingData = { [K in SettingKey]: { key: K; value: SettingValue<K> } }[SettingKey];

/** True when `key` is a setting key this build knows (one of `SETTING_VALUE_SCHEMAS`). */
export function isKnownSettingKey(key: string): key is SettingKey {
  return Object.hasOwn(SETTING_VALUE_SCHEMAS, key);
}

/** Minimal shape `resolveSettings` needs from a setting record. */
export interface SettingRecordLike {
  readonly hlc: string;
  readonly deleted: boolean;
  readonly data: SettingRecordData | null;
}

/**
 * Resolve all synced settings: stored values (latest hlc wins per key) over the defaults.
 * Deleted records, records under unknown keys (kept in storage, ignored here) and values that
 * fail their key's validation fall back to the default.
 *
 * Migration of the legacy `minUnit` (2026-10b §2.3, derived on read, storage untouched): a
 * missing `timeStepMin` or `minDurationMin` derives from a stored `minUnit` — the minimum
 * duration takes its value, the step becomes 30 (15 when `minUnit` is 15) — and without one
 * both fall back to 30. Read-time normalisation: a minimum duration that is no longer a
 * multiple of the step (two devices editing concurrently) rises to the smallest legal value,
 * and the effective default duration is at least the minimum duration, snapped to the step.
 */
export function resolveSettings(records: readonly SettingRecordLike[]): Settings {
  const ordered = [...records].sort((a, b) => (a.hlc < b.hlc ? -1 : a.hlc > b.hlc ? 1 : 0));
  let resolved: Settings = { ...SETTING_DEFAULTS };
  const present = new Set<SettingKey>();
  for (const record of ordered) {
    const data = record.data;
    if (record.deleted || data === null || !isKnownSettingKey(data.key)) continue;
    const parsed = SETTING_VALUE_SCHEMAS[data.key].safeParse(data.value);
    if (!parsed.success) continue;
    resolved = { ...resolved, [data.key]: parsed.data };
    present.add(data.key);
  }
  const legacy = present.has(SettingKey.MinUnit) ? resolved.minUnit : null;
  const derivedStep: TimeStep =
    legacy === null
      ? SETTING_DEFAULTS.timeStepMin
      : legacy === MinUnit.Min15
        ? TimeStep.Min15
        : TimeStep.Min30;
  const step = present.has(SettingKey.TimeStepMin) ? resolved.timeStepMin : derivedStep;
  const storedMinDuration = present.has(SettingKey.MinDurationMin)
    ? resolved.minDurationMin
    : (legacy ?? SETTING_DEFAULTS.minDurationMin);
  const minDuration = normalizeMinDuration(step, storedMinDuration);
  const defaultDuration = Math.max(snap(resolved.defaultDurationMin, step), step, minDuration);
  const legacyDayStart = present.has(SettingKey.CheckinLegacyDayStartMin)
    ? resolved.checkinLegacyDayStartMin
    : resolved.dayStartMin;
  return {
    ...resolved,
    timeStepMin: step,
    minDurationMin: minDuration,
    defaultDurationMin: defaultDuration,
    checkinLegacyDayStartMin: legacyDayStart,
    // Stored in the order the user picked; read normalised (distinct, ascending) and de-duplicated.
    defaultLeadDays: normalizeLeadDays(resolved.defaultLeadDays),
    holidayPacks: [...new Set(resolved.holidayPacks)],
  };
}

/** Validate a single setting value; returns the typed value or null when invalid. */
export function parseSettingValue<K extends SettingKey>(key: K, value: unknown): SettingValue<K> | null {
  const parsed = SETTING_VALUE_SCHEMAS[key].safeParse(value);
  return parsed.success ? (parsed.data as SettingValue<K>) : null;
}
